'use client';

import { useEffect, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface PlatformCounts {
  shopify: number;
  wordpress: number;
  woocommerce: number;
  html: number;
  nextjs: number;
  webflow: number;
}

interface PlatformSectionProps {
  platformCounts: PlatformCounts;
}

export default function PlatformSection({ platformCounts }: PlatformSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);

  const platforms = [
    {
      id: 'shopify',
      name: 'Shopify',
      count: platformCounts.shopify,
      description: 'High-converting storefronts built for Shopify\'s liquid theme engine.',
      color: '#96BF48',
      bg: '#F0F7E6',
      darkBg: '#1A2410',
      icon: 'ShoppingBagIcon',
      size: 'large',
    },
    {
      id: 'wordpress',
      name: 'WordPress',
      count: platformCounts.wordpress,
      description: 'Flexible themes for blogs, portfolios, and business sites.',
      color: '#21759B',
      bg: '#E8F4F8',
      darkBg: '#0E1E24',
      icon: 'DocumentTextIcon',
      size: 'medium',
    },
    {
      id: 'woocommerce',
      name: 'WooCommerce',
      count: platformCounts.woocommerce,
      description: 'Full-featured ecommerce templates with WooCommerce integration.',
      color: '#7F54B3',
      bg: '#F3EEF9',
      darkBg: '#1A1226',
      icon: 'CubeIcon',
      size: 'medium',
    },
    {
      id: 'html',
      name: 'HTML / CSS',
      count: platformCounts.html,
      description: 'Pure HTML5 & CSS3 templates — no dependencies, instant launch.',
      color: '#E8633A',
      bg: '#FDF0EB',
      darkBg: '#2A1A13',
      icon: 'CodeBracketIcon',
      size: 'large',
    },
    {
      id: 'nextjs',
      name: 'Next.js',
      count: platformCounts.nextjs,
      description: 'Modern React-based templates with SSR and App Router support.',
      color: '#000000',
      bg: '#F5F5F5',
      darkBg: '#1A1A1A',
      icon: 'BoltIcon',
      size: 'small',
    },
    {
      id: 'webflow',
      name: 'Webflow',
      count: platformCounts.webflow,
      description: 'Pixel-perfect Webflow templates ready to clone and customize.',
      color: '#4353FF',
      bg: '#ECEEFF',
      darkBg: '#101228',
      icon: 'SwatchIcon',
      size: 'small',
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );
    sectionRef.current?.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-24 lg:py-32"
      style={{ background: 'var(--bg-primary)' }}
    >
      <div className="max-w-8xl mx-auto px-6">
        <div className="reveal mb-14">
          <span
            className="text-2xs font-bold uppercase tracking-[0.2em] mb-3 block"
            style={{ color: 'var(--accent)' }}
          >
            Browse by Platform
          </span>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <h2
              className="font-display font-light text-4xl lg:text-6xl leading-tight"
              style={{ color: 'var(--text-primary)' }}
            >
              Every platform,{' '}
              <span className="italic">covered.</span>
            </h2>
            <Link
              href="/templates"
              className="flex items-center gap-2 text-sm font-semibold shrink-0"
              style={{ color: 'var(--accent)' }}
            >
              View all templates
              <Icon name="ArrowRightIcon" size={14} />
            </Link>
          </div>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 auto-rows-[180px]">
          {/* Large Shopify */}
          <Link
            href="/templates"
            className="reveal col-span-2 row-span-2 rounded-3xl p-6 flex flex-col justify-between group overflow-hidden relative cursor-pointer transition-transform duration-300 hover:scale-[1.02]"
            style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
          >
            <div>
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
                style={{ background: platforms[0].bg, color: platforms[0].color }}
              >
                <Icon name={platforms[0].icon as 'ShoppingBagIcon'} size={24} />
              </div>
              <h3 className="font-display text-3xl font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                {platforms[0].name}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                {platforms[0].description}
              </p>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold" style={{ color: platforms[0].color }}>
                {platforms[0].count} templates
              </span>
              <Icon
                name="ArrowRightIcon"
                size={18}
                className="transform group-hover:translate-x-1 transition-transform"
                style={{ color: platforms[0].color } as React.CSSProperties}
              />
            </div>
          </Link>

          {/* Medium cards */}
          {platforms.slice(1, 5).map((platform, i) => (
            <Link
              key={platform.id}
              href="/templates"
              className={`reveal col-span-${platform.size === 'large' ? 2 : 1} rounded-2xl p-5 flex flex-col justify-between group cursor-pointer transition-transform duration-300 hover:scale-[1.02]`}
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border)',
                gridColumn: platform.size === 'large' ? 'span 2' : 'span 1',
              }}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                style={{ background: platform.bg, color: platform.color }}
              >
                <Icon name={platform.icon as 'DocumentTextIcon'} size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-base mb-1" style={{ color: 'var(--text-primary)' }}>
                  {platform.name}
                </h3>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                  {platform.description}
                </p>
              </div>
              <span className="text-xs font-semibold mt-2" style={{ color: platform.color }}>
                {platform.count} templates →
              </span>
            </Link>
          ))}

          {/* Last small */}
          <Link
            href="/templates"
            className="reveal col-span-1 rounded-2xl p-5 flex flex-col justify-between group cursor-pointer transition-transform duration-300 hover:scale-[1.02]"
            style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
              style={{ background: platforms[5].bg, color: platforms[5].color }}
            >
              <Icon name={platforms[5].icon as 'SwatchIcon'} size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-base mb-1" style={{ color: 'var(--text-primary)' }}>
                {platforms[5].name}
              </h3>
              <span className="text-xs font-semibold mt-1 block" style={{ color: platforms[5].color }}>
                {platforms[5].count} templates →
              </span>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}